/**
 * useFounderMode — Patch 37.4.1 Addendum (Authorization Lockdown)
 *                  + Patch 37.4.7 (Founder-Default Fix).
 *
 * Provider / Router / Memory panels + cost/diagnostic ribbons are
 * Emergent-internal surfaces — they must never render for an ordinary
 * customer, no matter what URL they type or what value they poke into
 * ``sessionStorage``.
 *
 * ─── Two-part gate ──────────────────────────────────────────────
 *
 *   1. Authorization  (server-confirmed, non-forgeable):
 *      the current session must satisfy at least one of
 *        * ``user.is_dev_user === true``, OR
 *        * the authenticated membership for the ACTIVE workspace
 *          has ``role ∈ {owner, admin}``.
 *      Memberships come from ``/api/v1/auth/me`` and are stored in
 *      ``useAuthStore`` — never derived from the query string, never
 *      derived from ``Workspace.owner_user_id``, never derived from
 *      any browser-side state the caller can mutate.
 *
 *   2. Preference     (client-side, sticky, DEFAULT-ON for founders):
 *      An authorized founder sees Founder Mode by default. Adding
 *      ``?founder=0`` opts out (helpful for previewing the customer
 *      surface); ``?founder=1`` re-opts-in. The choice persists in
 *      ``sessionStorage`` across in-tab navigation. When authorization
 *      does not hold, both the URL parameter AND the stored preference
 *      are ignored — a customer cannot flip the switch by typing a URL,
 *      and a stale flag from a prior authorized session cannot leak
 *      across a workspace switch.
 *
 * Founder Mode is a **display gate**, not an authorization boundary.
 * Every endpoint the tab talks to enforces real server-side RBAC.
 * The purpose of hiding the tabs is trust — a customer who just
 * created their account should not be greeted with "Providers",
 * "Router", and "Memory" pages they can't act on.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAuthStore } from "@/entities/auth/store";
import { useCurrentWorkspace } from "@/entities/workspace";
const LS_KEY = "cz.founder-mode.optout";
function readOptOut(): boolean {
  try {
    return window.sessionStorage.getItem(LS_KEY) === "1";
  } catch {
    return false;
  }
}

function writeOptOut(off: boolean): void {
  try {
    if (off) window.sessionStorage.setItem(LS_KEY, "1");
    else window.sessionStorage.removeItem(LS_KEY);
  } catch {
    /* no-op */
  }
}

export function useFounderMode(): boolean {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const workspace = useCurrentWorkspace();

  // Server-confirmed authorization. NEVER trust the URL, NEVER trust
  // ``workspace.owner_user_id`` — the ONLY source of truth is the
  // membership collection returned by ``/api/v1/auth/me``.
  const isAuthorized = user?.is_dev_user === true;

  // Patch 37.4.12 · Hard demo lockdown. A demo workspace can never
  // enter Founder Mode — no matter the caller's role, ``is_dev_user``
  // state, URL parameter, or sessionStorage value. The
  // ``is_demo_workspace`` flag is server-stamped at workspace
  // creation (see ``services/demo_service.py``) and refreshed on
  // every ``/demo/login`` idempotent seed.
  const isPublicDemo = Boolean(
    (workspace?.settings as unknown as { is_demo_workspace?: boolean } | undefined)
      ?.is_demo_workspace,
  );
  const authorizedInNonDemoContext = isAuthorized && !isPublicDemo;

  // Sticky preference — stored as an OPT-OUT flag. Authorized founders
  // see Founder Mode by default; only an explicit ``?founder=0`` (or a
  // previously stored opt-out in this tab) hides the surface.
  const [optedOut, setOptedOut] = useState<boolean>(readOptOut);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("founder");

    // Explicit opt-out. Only accepted from authorized non-demo callers
    // — the demo lockdown means the toggle is meaningless on a demo
    // workspace and must not be persisted.
    if (raw === "0" && authorizedInNonDemoContext) {
      writeOptOut(true);
      setOptedOut(true);
      return;
    }

    // Explicit opt-in — writeOptOut(false) so a subsequent authorized
    // session picks up the preference. In a demo context the final
    // return still gates on ``authorizedInNonDemoContext`` so the flag
    // is inert.
    if (raw === "1") {
      writeOptOut(false);
      setOptedOut(false);
    }
  }, [location.search, authorizedInNonDemoContext]);

  // Belt-and-braces: whenever the session becomes unauthorized (e.g.
  // workspace switch, sign-out, role revoked, or moved into a demo
  // workspace), forget the opt-out flag so the next authorized
  // non-demo session in the same tab starts fresh.
  useEffect(() => {
    if (!authorizedInNonDemoContext && optedOut) {
      writeOptOut(false);
      setOptedOut(false);
    }
  }, [authorizedInNonDemoContext, optedOut]);

  return authorizedInNonDemoContext && !optedOut;
}
